import { useState } from 'react';

import 'react-phone-number-input/style.css';
import Input from 'react-phone-number-input';

import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { FormFieldIcon } from '@components/core-ui/form-elements/FormFieldIcon';
import { FormFieldRoot } from '@components/core-ui/form-elements/FormFieldRoot';
import ErrorIcon from '@components/icons/FormError.svg';
import { TProps } from './types/TProps';

const phoneInputWrapperClassName = [
  'phone-input-wrapper',
  '[&_.input]:inline-flex [&_.input]:h-10 [&_.input]:max-w-full [&_.input]:flex-[1_1_auto] [&_.input]:items-center [&_.input]:rounded-[5px] [&_.input]:border-0 [&_.input]:bg-[var(--color-form-field-bg,var(--color-surface-muted))] [&_.input]:px-4 [&_.input]:text-xs [&_.input]:font-medium [&_.input]:leading-3',
  '[&_.input_input]:cursor-text [&_.input_input]:border-0 [&_.input_input]:bg-[var(--color-form-field-bg,var(--color-surface-muted))] [&_.input_input]:text-[var(--color-form-field-text,var(--color-text-muted))]',
  '[&_.PhoneInput--disabled]:pointer-events-none',
  '[&_.input_input::placeholder]:font-medium [&_.input_input::placeholder]:text-[var(--color-form-field-placeholder,var(--color-text-muted))] [&_.input_input::placeholder]:opacity-100',
  '[&_.input_input:hover]:outline-none [&_.input_input:focus]:outline-none',
  '[&_.input:hover:not(:disabled)]:shadow-[0_0_0_1px_var(--color-form-field-focus-ring,var(--color-nav-active-bg))] [&_.input:hover:not(:disabled)]:outline-none [&_.input:focus-within]:shadow-[0_0_0_1px_var(--color-form-field-focus-ring,var(--color-nav-active-bg))] [&_.input:focus-within]:outline-none',
  '[&_.PhoneInput--disabled]:!bg-[var(--color-form-field-disabled-bg,var(--color-surface-muted))] [&_.PhoneInput--disabled]:!text-[var(--color-form-field-disabled-text,var(--color-text-muted))] [&_.PhoneInput--disabled]:!shadow-none [&_.PhoneInput--disabled]:!outline-none',
  '[&_.PhoneInput--disabled_.PhoneInputInput]:!bg-[var(--color-form-field-disabled-bg,var(--color-surface-muted))] [&_.PhoneInput--disabled_.PhoneInputInput]:!text-[var(--color-form-field-disabled-text,var(--color-text-muted))] [&_.PhoneInput--disabled_.PhoneInputInput]:![-webkit-text-fill-color:var(--color-form-field-disabled-text,var(--color-text-muted))]',
  '[&_.PhoneInput--disabled_.PhoneInputInput::placeholder]:!text-[var(--color-form-field-disabled-placeholder,var(--color-form-field-disabled-text,var(--color-text-muted)))] [&_.PhoneInput--disabled_.PhoneInputInput::placeholder]:!opacity-100',
  '[&_.PhoneInput--disabled_.PhoneInputCountrySelectArrow]:![--PhoneInputCountrySelectArrow-color:var(--color-form-field-disabled-icon,var(--color-form-field-disabled-text,var(--color-text-muted)))] [&_.PhoneInput--disabled_.PhoneInputCountrySelectArrow]:!opacity-60',
  '[&_.PhoneInput--disabled_.PhoneInputCountryIcon]:!opacity-60',
  '[&_.input.with-icon]:pr-[45px]',
  '[&_.input.error]:!shadow-[0_0_0_1px_var(--color-form-field-error)] [&_.input.error]:!outline-none',
  '[&_.input_.PhoneInputCountry]:mr-2.5',
].join(' ');

const phoneIconClassName = 'right-[5px] h-auto w-auto';

export const PhoneInput = ({
  icon: Icon,
  label,
  onChange,
  onIconClick,
  value = '',
  placeholder = '',
  required = false,
  disabled = false,
  error = false,
  className = 'form-input w-full',
  defaultCountry = 'US',
  handleBlur,
  size,
}: TProps) => {
  const defaultTitle = `${label ?? placeholder}${required ? ' *' : ''}`;
  const placeholderTitle = placeholder ? placeholder : defaultTitle;
  const [title, setTitle] = useState(defaultTitle);

  const onFocus = () => {
    setTitle(placeholderTitle);
  };

  const onBlur = () => {
    setTitle(defaultTitle);
    handleBlur?.();
  };

  const renderIcon = () => {
    if (!Icon) {
      return null;
    }
    if (error) {
      if (!onIconClick) {
        return (
          <FormFieldIcon className={phoneIconClassName}>
            <ErrorIcon />
          </FormFieldIcon>
        );
      }
      return (
        <FormFieldIcon clickable className={phoneIconClassName}>
          <IconButtonClean type="button" onClick={onIconClick}>
            <ErrorIcon />
          </IconButtonClean>
        </FormFieldIcon>
      );
    }
    if (!onIconClick) {
      return (
        <FormFieldIcon className={phoneIconClassName}>
          <Icon />
        </FormFieldIcon>
      );
    }
    return (
      <FormFieldIcon clickable className={phoneIconClassName}>
        <IconButtonClean type="button" onClick={onIconClick}>
          <Icon />
        </IconButtonClean>
      </FormFieldIcon>
    );
  };

  return (
    <FormFieldRoot disabled={disabled} className={[phoneInputWrapperClassName, className].filter(Boolean).join(' ')}>
      <Input
        initialValueFormat="national"
        international={false}
        className={`phone-input input ${Icon || error ? 'with-icon' : ''} ${error ? 'error' : ''}`}
        onChange={(value) => onChange((value || '').toString())}
        onFocus={onFocus}
        onBlur={onBlur}
        defaultCountry={defaultCountry}
        name="phone"
        placeholder={title}
        value={value}
        disabled={disabled}
        required={required}
        size={size}
      />
      {renderIcon()}
    </FormFieldRoot>
  );
};
